import { useCallback, useEffect, useState } from "react";
import { Gift, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

const dollars = (units) => `$${(Number(units || 0) / 1000).toFixed(2)}`;
const kinds = [["deposit", "Deposit cashback"], ["referral", "Referral cashback"]];

export function CashbackPanel({ dark, onClaim, refreshKey }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    try { setData((await api.get("/me/cashback")).data); setError(""); }
    catch (err) { setError(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => { window.removeEventListener("focus", refresh); document.removeEventListener("visibilitychange", refresh); };
  }, [load]);
  async function claim(kind) {
    setBusy(kind);
    try {
      const result = (await api.post("/me/cashback/claim", { kind })).data;
      setData(result);
      toast.success(result.credited ? `${dollars(result.credited)} added to wallet` : "No unclaimed cashback");
      await onClaim?.();
    } catch (err) { toast.error(formatApiError(err)); await load(); }
    finally { setBusy(""); }
  }
  return (
    <section aria-label="Cashback" className={`mb-6 border-y py-5 ${dark ? "border-white/10" : "border-slate-200"}`}>
      <h2 className="mb-4 flex items-center gap-2 text-xl font-bold"><Gift className="h-5 w-5 text-emerald-500" />Cashback</h2>
      {error && <div role="alert" className="mb-3 text-sm text-red-500">{error}<button type="button" className="ml-3 underline" onClick={load}>Retry</button></div>}
      <div className="grid gap-5 sm:grid-cols-2">
        {kinds.map(([kind, title]) => <div key={kind} data-testid={`cashback-${kind}`}>
          <div className="flex flex-wrap items-center justify-between gap-2"><h3 className="font-semibold">{title}</h3><span className="text-sm">{data ? `${data[kind].percent}%${data[kind].enabled ? "" : " (paused)"}` : "..."}</span></div>
          <p className="my-2 text-2xl font-bold tabular-nums">{data ? dollars(data[kind].available) : "..."}</p>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className={`text-sm ${dark ? "text-slate-400" : "text-slate-600"}`}>Claimed {dollars(data?.[kind]?.claimed)}</span>
            <button type="button" onClick={() => claim(kind)} disabled={!data?.[kind]?.available || Boolean(busy)} className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-bold text-white disabled:opacity-40"><Gift className="h-4 w-4" />{busy === kind ? "Claiming..." : "Claim"}</button>
          </div>
        </div>)}
      </div>
    </section>
  );
}

export function CashbackSettingsPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => { api.get("/admin/cashback").then(r => setData(r.data)).catch(e => setError(formatApiError(e))); }, []);
  async function save(event) {
    event.preventDefault(); setBusy(true); setError("");
    try {
      const body = Object.fromEntries(kinds.flatMap(([kind]) => [[`${kind}_enabled`, data[`${kind}_enabled`]], [`${kind}_percent`, Number(data[`${kind}_percent`])]]));
      setData((await api.put("/admin/cashback", body)).data); toast.success("Cashback settings saved");
    } catch (err) { setError(formatApiError(err)); }
    finally { setBusy(false); }
  }
  return <form onSubmit={save} className="mb-6 border-y border-white/10 py-5" aria-label="Cashback settings">
    <h2 className="mb-4 flex items-center gap-2 text-lg font-bold text-white"><Gift className="h-5 w-5 text-emerald-400" />Wallet cashback</h2>
    {error && <p role="alert" className="mb-3 text-sm text-red-400">{error}</p>}
    {!data ? <p className="text-sm text-slate-400">Loading...</p> : <>
      <div className="grid gap-5 sm:grid-cols-2">{kinds.map(([kind, title]) => <fieldset key={kind}>
        <label className="mb-3 flex items-center gap-2 font-semibold"><input type="checkbox" checked={data[`${kind}_enabled`]} onChange={e => setData({ ...data, [`${kind}_enabled`]: e.target.checked })} />{title}</label>
        <label className="flex items-center gap-3 text-sm">Percentage<input aria-label={`${title} percentage`} type="number" required min="0" max="100" step="0.01" value={data[`${kind}_percent`]} onChange={e => setData({ ...data, [`${kind}_percent`]: e.target.value })} className="w-28 rounded-lg border border-white/20 bg-black/30 px-3 py-2 text-white" />%</label>
      </fieldset>)}</div>
      <button type="submit" disabled={busy} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 font-semibold text-white disabled:opacity-40"><Save className="h-4 w-4" />{busy ? "Saving..." : "Save cashback"}</button>
    </>}
  </form>;
}
